import React, { useEffect, useState, useRef } from 'react';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { holeDataCache } from './holeDataCache';

const assayColumns = {
  'HoleID': 'Hole_ID',
  'from': 'Depth_From',
  'to': 'Depth_To',
  'grade': 'Au_ppm',
};

export default function CachedHoleRender({ key, name, hole_url, assay_url, dataset, holetype, visible }) {
  const [combinedData, setCombinedData] = useState({});
  const [filterData, setFilterData] = useState({});
  const [filteredHoleIds, setFilteredHoleIds] = useState([]);
  const [holeCount, setHoleCount] = useState(0);
  const [assayCount, setAssayCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // Get data from cache (loads once, then uses cache)
        const allData = await holeDataCache.getData(hole_url, assay_url);
        
        // Filter data for specific dataset and hole type
        const filteredData = holeDataCache.getFilteredData(hole_url, assay_url, dataset, holetype);
        
        setCombinedData(filteredData);
        setLoading(false);
      } catch (error) {
        console.error('Error loading hole data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, [hole_url, assay_url, dataset, holetype]);

  useEffect(() => {
    const filteredData = filteredHoleIds.length > 0
      ? Object.keys(combinedData).reduce((acc, key) => {
          if (filteredHoleIds.includes(key)) {
            acc[key] = combinedData[key];
          }
          return acc;
        }, {})
      : combinedData;

    setHoleCount(Object.keys(filteredData).length);
    let assayNumber = 0;
    Object.keys(filteredData).forEach((hole, j) => {
      const data = filteredData[hole].assays;
      assayNumber += data.length;
    });
    setAssayCount(assayNumber);
    
    setFilterData(filteredData);
  }, [combinedData, filteredHoleIds]);

  if (loading) {
    return null; // or a loading component
  }

  return (
    holeCount > 0 && visible &&
    <group visible={visible} id={key}>
      <Holes count={holeCount} holes={filterData}/>
      <SelectedHoleHighlight holes={filterData} />
      {assayCount > 0 && <Assays count={assayCount} holes={filterData}/>}
    </group>
  );
}

function Holes({ count, holes }) {
  const [instancesData, setInstancesData] = useState([]);
  
  useEffect(() => {
    let idata = [];
    Object.keys(holes).forEach((hole, i) => {
      const data = holes[hole];
      idata.push(data.data);
    });
    setInstancesData(idata);
  }, [holes]);

  return (
    <>
      {Object.keys(holes).map((holeId) => {
        const holeData = holes[holeId];
        // Convert points array to format expected by Line component
        const linePoints = holeData.points.map(p => [p.x, p.y, p.z]);

        return (
          <Line
            key={holeId}
            points={linePoints}
            closed={false}
            curveType="centripetal"
            userData={{ displayData: holeData.data, instancesData: instancesData }}
            lineWidth={2}
            color="#000000"
            name={holeId}
          />
        );
      })}
    </>
  );
}

function SelectedHoleHighlight({ holes }) {
  const [selectedHoleName, setSelectedHoleName] = useState(null);
  const [opacity, setOpacity] = useState(0.8);

  useEffect(() => {
    const unsub = require('../../store/clickStore.js').useClickStore.subscribe((state) => {
      if (state.clickType === 'info' && state.objectDetailsObject?.name && holes[state.objectDetailsObject.name]) {
        setSelectedHoleName(state.objectDetailsObject.name);
      } else {
        setSelectedHoleName(null);
      }
    });
    return () => unsub();
  }, [holes]);

  // Blink opacity
  useEffect(() => {
    let raf;
    const animate = () => {
      if (selectedHoleName) {
        const t = Date.now() * 0.001;
        setOpacity(0.5 + 0.4 * Math.sin(t * 4));
      }
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [selectedHoleName]);

  // Don't render if no hole selected
  if (!selectedHoleName || !holes[selectedHoleName]) {
    return null;
  }

  const holeData = holes[selectedHoleName];
  const linePoints = holeData.points.map(p => [p.x, p.y, p.z]);

  return (
    <Line
      points={linePoints}
      closed={false}
      curveType="centripetal"
      lineWidth={4}
      color="#FFD700"
      opacity={opacity}
      transparent
      name="HolesHighlight"
    />
  );
}

function Assays({ count, holes }) {
  const ref = useRef();
  const [colors, setColors] = useState(new Float32Array());
  const [instancesData, setInstancesData] = useState([]);

  useEffect(() => {
    let colorsArray = [];
    let idata = [];
    let i = 0;
    Object.keys(holes).forEach((hole, j) => {
      const data = holes[hole].assays;
      if (data.length > 0) {
        data.forEach((assay) => {
          const matrix = assay.matrix;
          const color = assay.color;
          const data = assay.data;
          idata.push({
            holeID: data[assayColumns.HoleID],
            from: data[assayColumns.from] + ' m',
            to: data[assayColumns.to] + ' m',
            grade: data[assayColumns.grade] + ' g/t'
          });
          ref.current.setMatrixAt(i, matrix);
          colorsArray.push(color.r, color.g, color.b);
          i++;
        });
      }
    });

    setInstancesData(idata);
    setColors(Float32Array.from(colorsArray));
    ref.current.instanceMatrix.needsUpdate = true;
  }, [holes]);

  return (
    <instancedMesh ref={ref} args={[null, null, count]} userData={{instancesData:instancesData}} name='Assays'>
      <cylinderGeometry args={[0.5, 0.5, 1, 8]} >
        <instancedBufferAttribute attach="attributes-color" args={[colors, 3]} />
      </cylinderGeometry>
      <meshLambertMaterial vertexColors toneMapped={false} />
    </instancedMesh>
  );
}
